import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="First run"` — the trial banner, the four things to do, and the three
 * empty-state cards.
 *
 * The trial is the product's whole onboarding promise and it is stated exactly as decided
 * (Q12): 30 minutes of talk time, no card, no expiry, **test calls counted the same as real
 * ones**, and the agent stops answering at zero rather than anything being charged. The
 * counter is talk time remaining, not days remaining, because there is no expiry to count.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Icon, Tag } from "@wetalk/ui";
import type { ReactNode } from "react";

import { GLYPH } from "../../shell/glyph.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY, billingQuery, firstRunQuery } from "../../api/query.js";
import { formatTrialRemaining } from "../../money.js";
import { ResendVerificationButton } from "../mail-landing/resend-verification.js";
import "./first-run.css";
import type { FirstRunTaskView } from "../../api/gateway.js";

/**
 * Where each task goes. Confirming an email has no console destination — the link is in the
 * email — so that task's button sends a new link (plan-014 review I-17).
 */
function destinationFor(task: FirstRunTaskView): string | null {
  switch (task.code) {
    case "create_voice_agent":
      return "/agents/new";
    case "connect_number":
      return "/numbers";
    case "add_card":
      return "/billing";
    case "verify_email":
      return null;
  }
}

const EMPTY_STATE = [
  {
    glyph: GLYPH.broadcast,
    get title() { return portalText("core", "first_run.no_calls_yet_dd2175"); },
    get dek() { return portalText("core", "first_run.live_conversations_will_appear_here_the_moment_your_number_rings_77d84e"); },
  },
  {
    glyph: GLYPH.chartLineUp,
    get title() { return portalText("core", "first_run.no_analytics_yet_76a4ab"); },
    get dek() { return portalText("core", "first_run.numbers_start_on_the_first_call_minutes_outcomes_and_what_people_97f113"); },
  },
  {
    glyph: GLYPH.receipt,
    get title() { return portalText("core", "first_run.no_spend_yet_18ad8a"); },
    get dek() { return portalText("core", "first_run.you_are_billed_only_while_an_agent_is_talking_idle_agents_cost_no_63685a"); },
  },
] as const;

export function FirstRunScreen(): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const firstRun = useQuery(firstRunQuery(gateway));
  const billing = useQuery(billingQuery(gateway));

  if (firstRun.data === undefined) {
    return <p className="c-note">{portalText("core", "first_run.setting_your_workspace_up_632934")}</p>;
  }

  const trialSeconds = billing.data?.trialSecondsRemaining ?? null;

  return (
    <section className="c-page c-fr">
      <p className="c-fr-kicker">
        {firstRun.data.accountName} · {firstRun.data.createdAtText}
      </p>
      <h1 className="c-fr-title">{portalText("core", "first_run.welcome_nothing_is_answering_yet_742712")}</h1>
      <p className="c-fr-lede">{portalText("core", "first_run.your_first_agent_takes_about_four_minutes_of_your_time_the_rest_h_64a4f1")}</p>

      <div className="c-fr-trial">
        <span className="c-fr-trial-body">
          <span className="c-fr-trial-kicker">{portalText("core", "first_run.free_trial_22357c")}</span>
          <span className="c-fr-trial-title">{portalText("core", "first_run.30_minutes_of_talk_time_no_card_no_expiry_cb1580")}</span>
          <span className="c-fr-trial-dek">{portalText("core", "first_run.tests_are_free_when_it_runs_out_the_agent_stops_answering")}</span>
        </span>
        <span className="c-fr-trial-figure">
          <span className="c-fr-trial-count">
            {trialSeconds === null ? "—" : formatTrialRemaining(trialSeconds)}
          </span>
          <span className="c-fr-trial-unit">{portalText("core", "first_run.remaining_398658")}</span>
        </span>
      </div>

      <p className="c-fr-section">{portalText("core", "first_run.four_things_to_do_0dfa60")}</p>
      <ul className="c-fr-tasks">
        {firstRun.data.task.map((task) => {

          const destination = destinationFor(task);
          return (
            <li className="c-fr-task" key={task.code}>
              <span className={task.done ? "c-fr-mark c-fr-mark-done" : "c-fr-mark"}>
                <Icon glyph={task.done ? GLYPH.check : GLYPH.circleDashed} size="lg" />
              </span>
              <span className="c-fr-task-body">
                <span className="c-fr-task-title">{task.title}</span>
                <span className="c-fr-task-dek">{task.dek}</span>
              </span>
              <span className="c-fr-task-action">
                {task.done ? (
                  <Tag tone="accent">{portalText("core", "first_run.done_e9b450")}</Tag>
                ) : destination === null ? (
                  <ResendVerificationButton refresh={[QUERY_KEY.firstRun]} label={task.actionLabel} />
                ) : (
                  <Link
                    to={destination}
                    className={
                      task.code === "create_voice_agent" ? "btn btn-primary" : "btn btn-secondary"
                    }
                  >
                    {task.actionLabel}
                  </Link>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="c-fr-empties">
        {EMPTY_STATE.map((one) => (
          <div className="c-fr-empty" key={one.title}>
            <span className="c-fr-empty-glyph">
              <Icon glyph={one.glyph} size="3xl" />
            </span>
            <p className="c-fr-empty-title">{one.title}</p>
            <p className="c-fr-empty-dek">{one.dek}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
